"use client";

/**
 * Countdown Renderer
 *
 * A full-width band counting down to the section's target date/time. Renders in both the
 * builder canvas and the public page (no DB reads). The band uses the page's primary brand
 * color as its background and a contrast-safe readable color for the eyebrow label and digits;
 * the accent palette color tints the digits when it has enough contrast on the band.
 *
 * The live value ticks at second resolution via `useClientNow`, so only this node re-renders
 * each second. Before the component mounts on the client it renders all-zeros (matching the
 * server render) to avoid a hydration mismatch.
 */

import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { getReadableTextColor, pickReadableOnBackground } from "@/features/landing-pages/_logic/color-utils";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useClientNow } from "../../../_hooks/useNow";
import { COUNTDOWN_ACCENT_MIN_CONTRAST, COUNTDOWN_TICK_MS } from "../../../_logic/constants";
import { getCountdownParts, wallClockToInstant } from "../../../_logic/utils/schedule-utils";
import type { CountdownComponent } from "../../../_types";
import { usePublicViewContext } from "../../public/PublicViewContext";

interface CountdownRendererProps {
  component: CountdownComponent;
}

/**
 * Renders the countdown band for a {@link CountdownComponent}.
 *
 * @param props - The countdown component to render.
 * @returns The countdown band element, or null on the public page when no target date is set.
 */
export function CountdownRenderer({ component }: CountdownRendererProps): React.ReactElement | null {
  // Hooks
  const { primaryColor, accentColor, timeZone } = useBuilderContext();
  const { isPublic } = usePublicViewContext();
  // Stop the 1s tick once the target passes — `hasStarted` is monotonic (the target is fixed and
  // time only moves forward), so a fired countdown shows static text without re-rendering forever.
  const [stopped, setStopped] = useState(false);
  const now = useClientNow(COUNTDOWN_TICK_MS, !stopped);

  // Derived values
  const eyebrow = component.title?.trim() || "Countdown";
  const expiredText = component.expiredText?.trim() || "The event has started.";

  const targetMs = useMemo(() => {
    if (!component.targetDate) return null;
    const zone = component.timeZone ?? timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    // layout_tree is persisted verbatim, so a malformed/legacy timeZone could reach Intl and throw
    // a RangeError at render. Guard it: an unresolvable zone falls back to "no target" (band hidden
    // on public, hint shown in builder) rather than crashing the whole page.
    try {
      return wallClockToInstant(component.targetDate, component.targetTime || "00:00", zone);
    } catch {
      return null;
    }
  }, [component.targetDate, component.targetTime, component.timeZone, timeZone]);

  // Contrast-safe colors derived from the brand palette. `pickReadableOnBackground` guarantees
  // the label/digit text meets contrast against the primary-colored band (white or near-black).
  const bandText = useMemo(() => pickReadableOnBackground(primaryColor), [primaryColor]);
  const digitColor = useMemo(() => {
    // Prefer the accent color for the digits, but only when it reads clearly on the band.
    const readableAccent = getReadableTextColor(accentColor, primaryColor, COUNTDOWN_ACCENT_MIN_CONTRAST);
    return readableAccent === accentColor ? accentColor : bandText;
  }, [accentColor, primaryColor, bandText]);

  // Single source of truth for the time delta so `hasStarted` and the displayed digits can never
  // disagree (e.g. band shows "started" while digits show a non-zero value).
  const remainingMs = now != null && targetMs != null ? targetMs - now : 0;
  const hasStarted = now != null && targetMs != null && remainingMs <= 0;
  const parts = getCountdownParts(remainingMs);

  // Effects — freeze the clock the moment the countdown reaches zero (pauses the interval above).
  useEffect(() => {
    if (hasStarted) setStopped(true);
  }, [hasStarted]);

  const segments = [
    { value: parts.days, label: "Days" },
    { value: parts.hours, label: "Hours" },
    { value: parts.minutes, label: "Minutes" },
    { value: parts.seconds, label: "Seconds" },
  ];

  // An unconfigured countdown (no target date) shows a builder hint, but renders nothing on the
  // public page so visitors never see a frozen all-zeros band (mirrors VideoRenderer).
  if (isPublic && !targetMs) return null;

  // Render
  return (
    <div className="w-full px-4 py-10" style={{ backgroundColor: primaryColor }}>
      <div className="mx-auto flex max-w-4xl flex-col items-center gap-6">
        <p className="text-center text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: bandText }}>
          {eyebrow}
        </p>

        {hasStarted ? (
          <p className="text-center text-2xl font-semibold" style={{ color: bandText }} aria-live="polite">
            {expiredText}
          </p>
        ) : (
          <div
            className="flex flex-wrap items-start justify-center gap-x-8 gap-y-4 sm:gap-x-12"
            role="group"
            aria-label={`Time remaining until ${eyebrow}`}
          >
            {segments.map((segment) => (
              <div key={segment.label} className="flex flex-col items-center">
                <span
                  className="text-5xl font-bold tabular-nums sm:text-6xl"
                  style={{ color: digitColor }}
                  aria-live="off"
                >
                  {segment.value}
                </span>
                <span className="mt-1 text-sm font-medium" style={{ color: bandText }}>
                  {segment.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Builder-only hint when no target date is set yet */}
        {!isPublic && !component.targetDate && (
          <p className="text-center text-xs font-medium" style={{ color: bandText }}>
            Set a target date in the editor to start the countdown.
          </p>
        )}
      </div>
    </div>
  );
}
